/**
 * DataCompletenessTile — compact Mission Control home nudge showing how much
 * of an org's value-chain data is still modeled vs measured. Self-hides unless
 * at least one stream is modeled (i.e. a benchmark baseline is in play), so the
 * home page stays clean for orgs running fully on their own data.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Database, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { useAuth } from '../../context/AuthContext';
import DataCompletenessMeter from '../benchmark/DataCompletenessMeter';

export default function DataCompletenessTile() {
  const navigate = useNavigate();
  const { viewAsOrg } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/admin/benchmark-baseline/completeness${q}`)
      .then((res) => { if (alive) setData(res?.data || null); })
      .catch((e) => { logger.error('completeness fetch failed:', e); if (alive) setData({ has_modeled: false }); });
    return () => { alive = false; };
  }, [viewAsOrg]);

  if (!data || !data.has_modeled) return null;

  const go = () => {
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    navigate(`/benchmark-baseline${q}`);
  };

  return (
    <Card
      className="border-cyan-200 bg-gradient-to-br from-white to-cyan-50/50 hover:shadow-md transition-shadow cursor-pointer"
      onClick={go}
      data-testid="data-completeness-tile"
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="shrink-0 h-11 w-11 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center">
          <Database className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">Data completeness</span>
          </div>
          <div className="mt-1">
            <DataCompletenessMeter completeness={data} compact />
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
      </CardContent>
    </Card>
  );
}
